import { useEffect, useState } from 'react';
import {
  subscribeBilling,
  subscribeTenant,
  subscribeUsage,
  type BillingDocResult,
  type TenantDocResult,
  type UsageDocResult,
} from '@/lib/firestoreTenantDetail';
import { currentYyyymm } from '@/lib/format';

export type { BillingDocResult };

export type TenantDetailState = {
  tenant: TenantDocResult;
  billing: BillingDocResult;
  usage: UsageDocResult;
  yyyymm: string;
};

/** Estado inicial: tudo `LOADING` para o mês dado. */
const initial = (yyyymm: string): TenantDetailState => ({
  tenant: { kind: 'LOADING' },
  billing: { kind: 'LOADING' },
  usage: { kind: 'LOADING' },
  yyyymm,
});

/**
 * Hook agregador da tela de detalhe do tenant: tenant, assinatura e uso do mês.
 *
 * Origem: sem issue — commit c5a7bd86a, 2026-04-26 16:52 +0200 (detalhe de
 * tenant com aba Visão geral, Sprint 2).
 *
 * Por quê: três listeners separados em vez de um `Promise.all` — cada parte
 * chega e falha sozinha (billing ausente não esconde o cabeçalho do tenant),
 * e a tela reflete na hora mudança feita por callable ou webhook.
 *
 * Contrato: cada campo é um `*DocResult` próprio (`LOADING`/`READY`/
 * `NOT_FOUND`/`ERROR`, ver `lib/firestoreTenantDetail.ts`). `yyyymm` é fixado
 * no primeiro render (`currentYyyymm()`, relógio do navegador — só para
 * escolher qual mês exibir, sem efeito persistente). Todos os listeners são
 * desfeitos no unmount ou na troca de `tenantId`.
 *
 * Usado por: `routes/TenantDetailPage.tsx` (cabeçalho, Visão geral, Cobrança,
 * Uso).
 *
 * Dados: `subscribeTenant` → `tenants/{tenantId}`; `subscribeBilling` →
 * `tenants/{tenantId}/billing/subscription` (fonte da verdade do plano
 * efetivo e do pricing congelado — CLAUDE.md); `subscribeUsage` →
 * `tenants/{tenantId}/billing/usage/months/{yyyymm}`. Schemas em
 * `@etiquetachef/types`, validados no `lib/`.
 *
 * Dicas: a virada de mês com a aba aberta não troca o `yyyymm` — recarregar a
 * página resolve; é intencional para o número não "zerar" sob o operador.
 */
export function useTenantDetail(tenantId: string): TenantDetailState {
  const [yyyymm] = useState(() => currentYyyymm());
  const [state, setState] = useState<TenantDetailState>(() => initial(yyyymm));

  useEffect(() => {
    setState(initial(yyyymm));
    const offTenant = subscribeTenant(tenantId, (r) => {
      setState((prev) => ({ ...prev, tenant: r }));
    });
    const offBilling = subscribeBilling(tenantId, (r) => {
      setState((prev) => ({ ...prev, billing: r }));
    });
    const offUsage = subscribeUsage(tenantId, yyyymm, (r) => {
      setState((prev) => ({ ...prev, usage: r }));
    });
    return (): void => {
      offTenant();
      offBilling();
      offUsage();
    };
  }, [tenantId, yyyymm]);

  return state;
}
